import React from 'react';
import { motion } from 'framer-motion';
import { Users, User, Target, BookOpen } from 'lucide-react';

const WHOP_GROUP = 'https://whop.com/dillon-fitness/training-camp-8b/';
const GUIDED_URL = 'https://link.fastpaydirect.com/payment-link/6a8747c4f9c8c807930b9318';
const PRIVATE_URL = 'https://link.fastpaydirect.com/payment-link/6a8746c8f9c8c807930b9316';
const FITNESS_GUIDE_URL = '#'; // TODO: replace with real link once shared

const IMG = {
  group: 'https://customer-assets-4nw71qhi.emergentagent.net/job_elite-coaching-15/artifacts/gkyk2ls1_group%20online.jpg',
  guided: 'https://customer-assets-4nw71qhi.emergentagent.net/job_elite-coaching-15/artifacts/zwvpukry_guided%20fitness.png',
  private: 'https://customer-assets-4nw71qhi.emergentagent.net/job_elite-coaching-15/artifacts/8qvh773v_private%20online.png',
};

const offers = [
  {
    key: 'group',
    icon: Users,
    title: 'Online Group Fitness',
    tagline: 'Build strength and consistency through a live workout led by The Dillon Blueprint.',
    bullets: [
      'One live workout each week',
      '30 minutes per workout',
      'Coach-led instruction',
      'Real-time form cues and support',
      'Train from anywhere',
      'Equipment required*',
    ],
    equipment: '*Required equipment: three pairs of dumbbells in light, medium and heavy weights, mini resistance bands, and a sturdy chair or workout bench.',
    cta: 'Join Group Fitness',
    link: WHOP_GROUP,
    variant: 'outline',
    image: IMG.group,
    featured: true,
  },
  {
    key: 'guided',
    icon: Target,
    title: 'Guided Fitness Coaching',
    tagline: 'Work out on your own schedule with a clear plan, accountability and ongoing coaching support.',
    bullets: [
      'Guided workout program',
      'Habit and consistency structure',
      'Weekly progress tracking',
      'Ongoing support and coaching tips',
      'Coaching call every two weeks',
    ],
    equipment: null,
    cta: 'Get Started',
    link: GUIDED_URL,
    variant: 'solid',
    image: IMG.guided,
  },
  {
    key: 'private',
    icon: User,
    title: 'Private Fitness Coaching',
    tagline: 'One-on-one private coaching from The Dillon Blueprint for someone serious about making measurable progress.',
    bullets: [
      'Two live one-on-one workouts each week',
      '45 minutes per workout',
      'Customized workout program',
      'Live form coaching and corrections',
      'Personal accountability',
      'Nutrition guidance',
    ],
    equipment: null,
    cta: 'Get Started',
    link: PRIVATE_URL,
    variant: 'solid',
    image: IMG.private,
  },
  {
    key: 'guide',
    icon: BookOpen,
    title: 'Fitness Guide',
    tagline: 'The self-paced blueprint for building strength on your own schedule.',
    bullets: [
      'Complete training framework',
      'Nutrition principles',
      'Habit and mindset guide',
      'Available soon',
    ],
    equipment: null,
    cta: 'Coming Soon',
    link: FITNESS_GUIDE_URL,
    variant: 'outline',
    comingSoon: true,
  },
];

export const CoachingOptions = () => {
  return (
    <section
      id="coaching"
      data-testid="coaching-options"
      className="bg-black py-14 sm:py-16 lg:py-20 px-6 border-b border-neutral-900"
    >
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7 }}
          className="text-center mb-10 lg:mb-14"
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="h-px w-16 bg-neutral-700" />
            <p className="text-gray-400 font-bold text-xs tracking-[0.35em] uppercase whitespace-nowrap">
              Coaching Options
            </p>
            <div className="h-px w-16 bg-neutral-700" />
          </div>
          <h2 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl uppercase leading-none">
            Choose The Support You Need.
          </h2>
          <p className="text-gray-400 text-sm sm:text-base mt-4 max-w-xl mx-auto">
            Four focused ways to build strength, structure, and consistency.
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5">
          {offers.map((o, i) => (
            <motion.div
              key={o.key}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.65, delay: i * 0.1 }}
              data-testid={`coaching-card-${o.key}`}
              className={`relative rounded-2xl border bg-neutral-950 flex flex-col card-hover-lift spotlight hover:border-neutral-600 transition-colors overflow-hidden ${
                o.featured ? 'border-gray-300' : 'border-neutral-800'
              }`}
              onMouseMove={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
                e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
              }}
            >
              {o.featured && (
                <span className="absolute top-4 left-4 z-10 text-[10px] font-bold tracking-[0.3em] uppercase text-black bg-gray-300 px-3 py-1 rounded">
                  Most Popular
                </span>
              )}

              {/* Image */}
              {o.image ? (
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={o.image}
                    alt={o.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-transparent to-transparent" />
                </div>
              ) : (
                <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-neutral-900 via-black to-neutral-900 flex items-center justify-center">
                  <o.icon className="w-16 h-16 text-gray-600" strokeWidth={1.2} />
                </div>
              )}

              <div className="p-6 lg:p-7 flex flex-col flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <o.icon className="w-6 h-6 text-gray-300" strokeWidth={1.5} />
                  <h3 className="font-display text-white text-xl uppercase tracking-wide leading-tight">
                    {o.title}
                  </h3>
                </div>

                <p className="text-gray-400 text-sm leading-relaxed mb-5">{o.tagline}</p>

                <ul className="space-y-2 mb-5">
                  {o.bullets.map((b) => (
                    <li key={b} className="text-gray-300 text-sm flex gap-2">
                      <span className="text-gray-500">•</span>
                      {b}
                    </li>
                  ))}
                </ul>

                {o.equipment && (
                  <p className="text-gray-500 text-xs uppercase tracking-[0.15em] mb-5">{o.equipment}</p>
                )}

                {o.comingSoon ? (
                  <button
                    type="button"
                    disabled
                    data-testid={`cta-${o.key}`}
                    className="mt-auto inline-flex items-center justify-center text-center font-bold uppercase tracking-wider text-xs px-6 py-4 rounded-lg border border-neutral-700 text-gray-500 cursor-not-allowed"
                  >
                    {o.cta}
                  </button>
                ) : (
                  <a
                    href={o.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`cta-${o.key}`}
                    className={`mt-auto inline-flex items-center justify-center text-center font-bold uppercase tracking-wider text-xs px-6 py-4 rounded-lg transition-colors ${
                      o.variant === 'solid'
                        ? 'btn-shine bg-white hover:bg-gray-200 text-black'
                        : 'border border-gray-500 hover:border-white text-white hover:bg-white hover:text-black'
                    }`}
                  >
                    <span className="relative z-10">{o.cta}</span>
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
